import { useMemo, useState } from "react";
import { Modal } from "./Modal";
import { api, ApiError } from "../lib/api";
import { useResource } from "../lib/hooks";

/**
 * The full quote editor: header + line items in one modal.
 *
 * A line is priced from the service you pick. For an NDIS nursing service the
 * rate is the day-typed cap x your charge % -- pick the day type (weekday /
 * evening / night / Sat / Sun / public holiday) and the correct cap resolves
 * automatically (the same six categories the invoice engine uses). Flat / per-km
 * / cancellation services price from their set rate. GST is applied only to
 * lines that aren't GST-free (most NDIS supports are), and only when the quote's
 * GST switch is on. Each line snapshots its item / cap / day-type / era so a
 * reprint stays true after caps change on 1 July.
 *
 * On save it upserts the header, then replaces its lines wholesale.
 */

interface ServiceRow {
    id: string; name: string; default_rate: string | null; unit: string | null;
    line_type: string | null; billing_role: string | null; ndis_reg_prefix: string | null;
    ndis_item_number: string | null; charge_percent: string | null; gst_free: boolean | null;
}
interface CompanyRow { id: string; name: string; }
interface ContactRow { id: string; first_name: string; last_name: string; company_id: string | null; }
interface NamedRow { name: string; }
interface NdisItem {
    item_number: string; billing_role: string | null; day_type: string | null;
    price_national: string | null; price_year: string;
}
interface SavedLine {
    id: string; quote_id: string; service: string; hours: string; rate: string; sort_order: number;
    line_type?: string | null; day_type?: string | null; ndis_item_number?: string | null;
    cap_amount?: string | null; price_year?: string | null; gst_free?: boolean | null;
}

export interface QuoteHeader {
    id?: string;
    reference?: string | null;
    status?: string;
    company_id?: string | null;
    contact_id?: string | null;
    inquiry_id?: string | null;
    valid_until?: string | null;
    payment_terms?: string | null;
    apply_gst?: boolean;
    gst_rate?: string | number;
    notes?: string | null;
}

interface LineDraft {
    key: string; id?: string;
    service: string; lineType: string; dayType: string;
    hours: string; rate: string; gstFree: boolean;
    billingRole: string | null; regPrefix: string; chargePercent: number | null;
    ndisItemNumber: string | null; capAmount: number | null; priceYear: string | null;
}

const STATUS = ["draft", "sent", "approved", "rejected", "invoiced", "paid"];
const DAY_TYPES: Array<[string, string]> = [
    ["weekday_day", "Weekday day"], ["weekday_evening", "Weekday evening"],
    ["weekday_night", "Weekday night"], ["saturday", "Saturday"],
    ["sunday", "Sunday"], ["public_holiday", "Public holiday"],
];
const currency = (n: number): string => n.toLocaleString("en-AU", { style: "currency", currency: "AUD" });
const uid = () => Math.random().toString(36).slice(2);
const num = (v: unknown): number => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

const blankLine = (): LineDraft => ({
    key: uid(), service: "", lineType: "hourly", dayType: "weekday_day",
    hours: "1", rate: "", gstFree: true,
    billingRole: null, regPrefix: "01", chargePercent: null,
    ndisItemNumber: null, capAmount: null, priceYear: null,
});

export function QuoteEditor({ quote, prefillLines, onClose, onSaved }: {
    quote: QuoteHeader | null;
    /** Lines to start with -- used by "Create quote from inquiry". */
    prefillLines?: Array<{ service: string; hours: string; rate: string }>;
    onClose: () => void;
    onSaved: () => void;
}) {
    const services = useResource<ServiceRow[]>("/services");
    const companies = useResource<CompanyRow[]>("/companies");
    const contacts = useResource<ContactRow[]>("/contacts");
    const paymentTerms = useResource<NamedRow[]>("/payment-terms");
    const ndisItems = useResource<NdisItem[]>("/ndis-items?limit=1000");

    const existingLines = useResource<SavedLine[]>(
        quote?.id ? `/quote-lines?quote_id=${quote.id}` : null
    );

    const [reference, setReference] = useState(quote?.reference ?? "");
    const [status, setStatus] = useState(quote?.status ?? "draft");
    const [companyId, setCompanyId] = useState(quote?.company_id ?? "");
    const [contactId, setContactId] = useState(quote?.contact_id ?? "");
    const [validUntil, setValidUntil] = useState((quote?.valid_until ?? "").slice(0, 10));
    const [terms, setTerms] = useState(quote?.payment_terms ?? "");
    const [applyGst, setApplyGst] = useState(Boolean(quote?.apply_gst));
    const [notes, setNotes] = useState(quote?.notes ?? "");
    const [lines, setLines] = useState<LineDraft[]>(
        (prefillLines ?? []).map((l) => ({ ...blankLine(), service: l.service, hours: l.hours, rate: l.rate }))
    );
    const [loadedLines, setLoadedLines] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const gstRate = num(quote?.gst_rate ?? 0.1) || 0.1;
    const serviceByName = useMemo(
        () => new Map((services.data ?? []).map((s) => [s.name, s])), [services.data]);

    // Cap lookups from the catalogue: keyed by role|budget-prefix|day-type, and by item number.
    const capForRole = useMemo(() => {
        const map = new Map<string, NdisItem>();
        for (const it of ndisItems.data ?? []) {
            if (it.billing_role && it.day_type) {
                map.set(`${it.billing_role}|${it.item_number.slice(0, 2)}|${it.day_type}`, it);
            }
        }
        return map;
    }, [ndisItems.data]);
    const itemByNumber = useMemo(
        () => new Map((ndisItems.data ?? []).map((it) => [it.item_number, it])), [ndisItems.data]);

    /** Resolve the NDIS-derived rate + snapshot for a line from its day type. */
    const rateFor = (l: LineDraft): { rate: number; cap: number | null; item: string | null; year: string | null } => {
        if (l.lineType !== "hourly") return { rate: num(l.rate), cap: null, item: l.ndisItemNumber, year: l.priceYear };
        if (l.billingRole && l.chargePercent != null) {
            const it = capForRole.get(`${l.billingRole}|${l.regPrefix}|${l.dayType}`);
            const cap = it?.price_national != null ? num(it.price_national) : null;
            return {
                rate: cap != null ? Math.round(cap * (l.chargePercent / 100) * 100) / 100 : num(l.rate),
                cap, item: it?.item_number ?? null, year: it?.price_year ?? null,
            };
        }
        if (l.ndisItemNumber) {
            const it = itemByNumber.get(l.ndisItemNumber);
            const cap = it?.price_national != null ? num(it.price_national) : null;
            return { rate: num(l.rate), cap, item: l.ndisItemNumber, year: it?.price_year ?? l.priceYear };
        }
        return { rate: num(l.rate), cap: l.capAmount, item: null, year: l.priceYear };
    };

    // Seed line drafts from the quote's saved lines, once, when they arrive.
    if (quote?.id && existingLines.data && !loadedLines) {
        setLines(existingLines.data
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((l) => ({
                key: uid(), id: l.id, service: l.service,
                lineType: l.line_type ?? "hourly", dayType: l.day_type ?? "weekday_day",
                hours: String(num(l.hours)), rate: String(num(l.rate)),
                gstFree: l.gst_free ?? true,
                billingRole: null, regPrefix: (l.ndis_item_number ?? "01").slice(0, 2),
                chargePercent: null, ndisItemNumber: l.ndis_item_number ?? null,
                capAmount: l.cap_amount != null ? num(l.cap_amount) : null,
                priceYear: l.price_year ?? null,
            })));
        setLoadedLines(true);
    }

    const offeredContacts = (contacts.data ?? []).filter((c) => !companyId || c.company_id === companyId);
    const setLine = (key: string, patch: Partial<LineDraft>) =>
        setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

    // Picking a service loads its charge model; the rate then follows the day type.
    const pickService = (key: string, name: string) => {
        const svc = serviceByName.get(name);
        if (!svc) { setLine(key, { service: name }); return; }
        const patch: Partial<LineDraft> = {
            service: name,
            lineType: svc.line_type ?? "hourly",
            gstFree: svc.gst_free ?? true,
            billingRole: svc.billing_role,
            regPrefix: svc.ndis_reg_prefix ?? "01",
            chargePercent: svc.charge_percent != null ? num(svc.charge_percent) : null,
            ndisItemNumber: svc.billing_role ? null : svc.ndis_item_number,
        };
        const resolved = rateFor({ ...blankLine(), ...patch, dayType: "weekday_day" } as LineDraft);
        setLine(key, {
            ...patch,
            rate: resolved.rate ? String(resolved.rate)
                : (svc.default_rate != null ? String(num(svc.default_rate)) : ""),
            capAmount: resolved.cap, priceYear: resolved.year,
        });
    };

    // Changing the day type re-resolves the cap-based rate for nursing lines.
    const pickDayType = (key: string, dayType: string) => {
        setLines((ls) => ls.map((l) => {
            if (l.key !== key) return l;
            const next = { ...l, dayType };
            if (l.lineType === "hourly" && l.billingRole && l.chargePercent != null) {
                const r = rateFor(next);
                return { ...next, rate: r.rate ? String(r.rate) : l.rate, capAmount: r.cap, priceYear: r.year };
            }
            return next;
        }));
    };

    const lineTotal = (l: LineDraft) => num(l.hours) * num(l.rate);
    const subtotal = lines.reduce((sum, l) => sum + lineTotal(l), 0);
    const taxable = lines.reduce((sum, l) => sum + (l.gstFree ? 0 : lineTotal(l)), 0);
    const gst = applyGst ? taxable * gstRate : 0;
    const total = subtotal + gst;

    const save = async () => {
        if (!reference.trim()) { setError("A quote reference is required."); return; }
        setBusy(true); setError(null);
        try {
            const body = {
                reference: reference.trim(), status,
                company_id: companyId || null, contact_id: contactId || null,
                inquiry_id: quote?.inquiry_id ?? null,
                valid_until: validUntil || null, payment_terms: terms || null,
                apply_gst: applyGst, notes: notes.trim() || null,
            };
            const saved = quote?.id
                ? await api.put<{ id: string }>(`/quotes/${quote.id}`, body)
                : await api.post<{ id: string }>("/quotes", body);
            const quoteId = saved.id;

            // Replace lines wholesale: a quote's lines are fully owned here.
            for (const old of existingLines.data ?? []) {
                await api.delete(`/quote-lines/${old.id}`);
            }
            let order = 0;
            for (const l of lines) {
                if (!l.service.trim() || !(num(l.hours) > 0)) continue;
                const r = rateFor(l);
                await api.post("/quote-lines", {
                    quote_id: quoteId, service: l.service, hours: num(l.hours),
                    rate: num(l.rate) || 0, sort_order: order++,
                    line_type: l.lineType,
                    day_type: l.lineType === "hourly" && l.billingRole ? l.dayType : null,
                    ndis_item_number: r.item, cap_amount: r.cap, price_year: r.year,
                    gst_free: l.gstFree,
                });
            }
            onSaved();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Could not save the quote");
        } finally {
            setBusy(false);
        }
    };

    const isNursing = (l: LineDraft) => l.lineType === "hourly" && !!l.billingRole;

    return (
        <Modal
            title={quote?.id ? `Edit ${quote.reference ?? "quote"}` : "New Quote"}
            width="wide"
            onClose={onClose}
            footer={
                <>
                    <button className="btn" disabled={busy} onClick={() => void save()}>
                        {busy ? "Saving…" : quote?.id ? "Save changes" : "Create quote"}
                    </button>
                    <button className="btn ghost" disabled={busy} onClick={onClose}>Cancel</button>
                </>
            }
        >
            {error && <div className="error-banner">{error}</div>}

            <div className="form-grid">
                <div className="field half">
                    <label>Quote reference <span className="req">*</span></label>
                    <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Q-2026-015" />
                </div>
                <div className="field half">
                    <label>Status</label>
                    <select value={status} onChange={(e) => setStatus(e.target.value)}>
                        {STATUS.map((s) => <option key={s} value={s}>{s[0]!.toUpperCase() + s.slice(1)}</option>)}
                    </select>
                </div>
                <div className="field half">
                    <label>Company</label>
                    <select value={companyId} onChange={(e) => { setCompanyId(e.target.value); setContactId(""); }}>
                        <option value="">—</option>
                        {(companies.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
                <div className="field half">
                    <label>Contact</label>
                    <select value={contactId} onChange={(e) => setContactId(e.target.value)}>
                        <option value="">—</option>
                        {offeredContacts.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                    </select>
                </div>
                <div className="field half">
                    <label>Valid until</label>
                    <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
                </div>
                <div className="field half">
                    <label>Payment terms</label>
                    <select value={terms} onChange={(e) => setTerms(e.target.value)}>
                        <option value="">—</option>
                        {(paymentTerms.data ?? []).map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
                    </select>
                </div>
            </div>

            <h3 style={{ margin: "1rem 0 0.4rem" }}>Line items</h3>
            <div className="table-wrap">
                <table>
                    <thead>
                        <tr><th style={{ minWidth: 180 }}>Service</th><th>Day type</th><th>Hours / qty</th><th>Rate ($)</th>
                            <th style={{ textAlign: "right" }}>Line total</th><th /></tr>
                    </thead>
                    <tbody>
                        {lines.map((l) => (
                            <tr key={l.key}>
                                <td>
                                    <select value={serviceByName.has(l.service) ? l.service : "__custom__"}
                                        onChange={(e) => e.target.value === "__custom__"
                                            ? setLine(l.key, { service: "" })
                                            : pickService(l.key, e.target.value)}>
                                        <option value="">— pick a service —</option>
                                        {(services.data ?? []).map((s) => (
                                            <option key={s.id} value={s.name}>
                                                {s.name}{s.billing_role ? ` (${s.billing_role} · ${s.charge_percent ?? 88}%)` : ""}
                                            </option>
                                        ))}
                                        <option value="__custom__">Other / free text…</option>
                                    </select>
                                    {!serviceByName.has(l.service) && (
                                        <input style={{ marginTop: 4 }} value={l.service} placeholder="Describe the service"
                                            onChange={(e) => setLine(l.key, { service: e.target.value })} />
                                    )}
                                    {!l.gstFree && <span className="chip info" style={{ marginTop: 4, marginLeft: 4 }}>+GST</span>}
                                </td>
                                <td>
                                    {isNursing(l) ? (
                                        <select value={l.dayType} onChange={(e) => pickDayType(l.key, e.target.value)}>
                                            {DAY_TYPES.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
                                        </select>
                                    ) : <span className="muted">—</span>}
                                </td>
                                <td><input type="number" min={0} step="0.25" value={l.hours} style={{ width: 90 }}
                                    onChange={(e) => setLine(l.key, { hours: e.target.value })} /></td>
                                <td><input type="number" min={0} step="0.01" value={l.rate} style={{ width: 100 }}
                                    onChange={(e) => setLine(l.key, { rate: e.target.value })} /></td>
                                <td className="mono" style={{ textAlign: "right" }}>{currency(lineTotal(l))}</td>
                                <td><button className="icon-btn danger" title="Remove line"
                                    onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>✕</button></td>
                            </tr>
                        ))}
                        {lines.length === 0 && (
                            <tr><td colSpan={6} className="empty">No lines yet — add one below.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <button className="btn ghost small" style={{ marginTop: 6 }}
                onClick={() => setLines((ls) => [...ls, blankLine()])}>
                ＋ Add line
            </button>

            <div className="quote-totals" style={{ marginTop: "1rem", maxWidth: 340, marginLeft: "auto" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Subtotal (ex GST)</span><span className="mono">{currency(subtotal)}</span>
                </div>
                <label className="check" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "0.35rem 0" }}>
                    <span><input type="checkbox" checked={applyGst} onChange={(e) => setApplyGst(e.target.checked)} />{" "}
                        Apply GST ({(gstRate * 100).toFixed(0)}% on taxable lines)</span>
                    <span className="mono">{currency(gst)}</span>
                </label>
                <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, borderTop: "1px solid var(--line)", paddingTop: 6 }}>
                    <span>Total</span><span className="mono">{currency(total)}</span>
                </div>
                {applyGst && taxable === 0 && <p className="help" style={{ marginTop: 4 }}>All lines are GST-free — no GST added.</p>}
                {!applyGst && <p className="help" style={{ marginTop: 4 }}>GST switched off — nothing taxed.</p>}
            </div>

            <div className="field" style={{ marginTop: "1rem" }}>
                <label>Notes</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
        </Modal>
    );
}
